import { useEffect, useState } from 'react';
import { useDataService } from '../../../../../services/ApiDataService';
import { TEST_TYPES_API } from '../../ServiceTestsConstants';

const UUID_REGEX_GENERIC = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUUID = (str) => {
    return UUID_REGEX_GENERIC.test(str);
};

/**
 * Hook for test type lookup via API
 *
 * Fetches a single test type by group name from the backend and exposes
 * its UUID (typeId) and typeTitle.
 *
 * @param {string} group - Test type group name (e.g., 'Web', 'bandwidth')
 * @returns {{
 * typeId: string|undefined,
 * typeTestBuilderListColumns: objects|undefined,
 * testTypeObject: object|null,
 * isLoading: boolean,
 * hasFetched: boolean,
 * error: Error|null
 * }}
 */
export default function useServiceTestTypeMapping(group) {
    const dataService = useDataService();
    const [typeData, setTypeData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!group) {
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        let fetchTestTypeFn;
        if (isUUID(group)) {
            fetchTestTypeFn = () =>
                dataService.get(`${TEST_TYPES_API}/${group}`).then((response) => {
                    if (!cancelled) {
                        setTypeData(Array.isArray(response) ? response : [response]);
                        setIsLoading(false);
                    }
                });
        } else {
            fetchTestTypeFn = () =>
                dataService
                    .get(TEST_TYPES_API, {
                        t: Date.now(),
                        query: JSON.stringify({ group }),
                    })
                    .then((response) => {
                        if (!cancelled) {
                            setTypeData(Array.isArray(response) ? response : [response]);
                            setIsLoading(false);
                        }
                    });
        }

        fetchTestTypeFn()
            .catch((err) => {
                if (!cancelled) {
                    setError(err);
                    setIsLoading(false);
                }
            })
            .finally(() => {
                setHasFetched(true);
            });

        return () => {
            cancelled = true;
        };
    }, [group, dataService]);

    return {
        testTypeArrayObject: typeData || [],
        isLoading,
        error,
        hasFetched,
    };
}
